import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { classList, subjectList, syllabusCategoryList } from "../../utils";
import { getDecodedToken } from "../../auth";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { generateRandomNo } from "../../functions";
import { useDispatch, useSelector } from "react-redux";
import { fetchSyllabus } from "../../redux/syllabusSlice";

function Syllabus() {
  const dispatch = useDispatch();
  const { data: syllabus, loading: fetchLoading } = useSelector(
    (state) => state.syllabus,
  );

  useEffect(() => {
    dispatch(fetchSyllabus());
  }, [dispatch]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [editingSyllabusId, setEditingSyllabusId] = useState(null);
  const [existingFileKey, setExistingFileKey] = useState("");

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const userId = user?.userId;
  const [formData, setFormData] = useState({
    Class: "",
    subject: "",
    category: "",
    file: null,
  });

  const resetForm = () => {
    setFormData({
      Class: "",
      subject: "",
      category: "",
      file: null,
    });
    setIsEditing(false);
    setEditingSyllabusId(null);
    setExistingFileKey("");
    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleEditClick = (syllabus) => {
    setFormData({
      Class: syllabus.Class,
      subject: syllabus.subject,
      category: syllabus.category,
      file: null, // Reset file input
    });
    setExistingFileKey(syllabus.file || ""); // Store existing file URL
    setEditingSyllabusId(syllabus.syllabusId);
    setIsEditing(true);

    // Open modal
    const modal = new window.bootstrap.Modal(document.querySelector(".modal"));
    modal.show();
  };

  const totalFields = Object.keys(formData).length;
  const filledFields = Object.values(formData).filter(
    (val) => typeof val === "string" && val.trim() !== "",
  ).length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let uploadedFileUrl = existingFileKey;
    const syllabusId = isEditing ? editingSyllabusId : generateRandomNo();

    try {
      if (formData.file) {
        const presignRes = await axios.post(
          `${server}/syllabus_pdf_presigned`,
          { fileName: [formData.file.name] },
        );

        const uploadDetails =
          presignRes.data["body-json"].body.uploadDetails[0];
        await axios.put(uploadDetails.signedUrl, formData.file, {
          headers: { "Content-Type": uploadDetails.contentType },
        });

        uploadedFileUrl = uploadDetails.key;
      }

      const payload = {
        userId,
        syllabusId,
        Class: formData.Class,
        subject: formData.subject,
        category: formData.category,
        file: uploadedFileUrl,
      };

      const endpoint = isEditing ? "updateSyllabus" : "add_syllabus_mongodb";

      await axios.post(`${server}/${endpoint}`, payload, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
        },
      });

      toast.success(`Syllabus ${isEditing ? "Updated" : "Submitted"}`, {
        position: "top-right",
      });

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      dispatch(fetchSyllabus());

      setFormData({
        Class: "",
        subject: "",
        category: "",
        file: null,
      });
      setIsEditing(false);
      setEditingSyllabusId(null);
      setExistingFileKey("");
      document.getElementById("fileInput").value = "";
    } catch (error) {
      console.error(error);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filteredRecords = syllabus.filter((syllabus) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(syllabus).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleExportAllSyllabusExcel = () => {
    if (!syllabus || syllabus.length === 0) {
      toast.error("No syllabus data to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const syllabusRows = syllabus.map((item) => ({
      "Syllabus ID": item.syllabusId || "",
      "Teacher ID": item.userId || "",
      Class: item.Class || "",
      Subject: item.subject || "",
      Category: item.category || "",
      File: item.file || "N/A",
      "Created At": formatDate(item.createdAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(syllabusRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Syllabus");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllSyllabus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportTeacherWiseSyllabusExcel = () => {
    if (!syllabus || syllabus.length === 0) {
      toast.error("No syllabus data to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const teacherMap = {};

    syllabus.forEach((item) => {
      const teacherId = item.userId || "Unknown";

      const row = {
        "Syllabus ID": item.syllabusId || "",
        "Teacher ID": teacherId,
        Class: item.Class || "",
        Subject: item.subject || "",
        Category: item.category || "",
        File: item.file || "N/A",
        "Created At": formatDate(item.createdAt),
      };

      if (!teacherMap[teacherId]) {
        teacherMap[teacherId] = [];
      }
      teacherMap[teacherId].push(row);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(teacherMap).forEach(([teacherId, rows]) => {
      const sheet = XLSX.utils.json_to_sheet(rows);
      const safeSheetName =
        teacherId.length > 31 ? teacherId.slice(0, 31) : teacherId;
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `TeacherWise_Syllabus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportClassWiseSyllabusExcel = () => {
    if (!syllabus || syllabus.length === 0) {
      toast.error("No syllabus data to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const classMap = {};

    syllabus.forEach((item) => {
      const className = item.Class || "Unknown";

      const row = {
        "Syllabus ID": item.syllabusId || "",
        "Teacher ID": item.userId || "",
        Class: className,
        Subject: item.subject || "",
        Category: item.category || "",
        File: item.file || "N/A",
        "Created At": formatDate(item.createdAt),
      };

      if (!classMap[className]) {
        classMap[className] = [];
      }
      classMap[className].push(row);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(classMap).forEach(([className, rows]) => {
      const sheet = XLSX.utils.json_to_sheet(rows);
      const safeSheetName =
        className.length > 31 ? className.slice(0, 31) : className;
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `ClassWise_Syllabus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportSubjectWiseSyllabusExcel = () => {
    if (!syllabus || syllabus.length === 0) {
      toast.error("No syllabus data to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const subjectMap = {};

    syllabus.forEach((item) => {
      const subject = item.subject || "Unknown";

      const row = {
        "Syllabus ID": item.syllabusId || "",
        "Teacher ID": item.userId || "",
        Class: item.Class || "",
        Subject: subject,
        Category: item.category || "",
        File: item.file || "N/A",
        "Created At": formatDate(item.createdAt),
      };

      if (!subjectMap[subject]) {
        subjectMap[subject] = [];
      }
      subjectMap[subject].push(row);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(subjectMap).forEach(([subject, rows]) => {
      const sheet = XLSX.utils.json_to_sheet(rows);
      const safeSheetName =
        subject.length > 31 ? subject.slice(0, 31) : subject;
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `SubjectWise_Syllabus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportCategoryWiseSyllabusExcel = () => {
    if (!syllabus || syllabus.length === 0) {
      toast.error("No syllabus data to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const categoryMap = {};

    syllabus.forEach((item) => {
      const category = item.category || "Unknown";

      const row = {
        "Syllabus ID": item.syllabusId || "",
        "Teacher ID": item.userId || "",
        Class: item.Class || "",
        Subject: item.subject || "",
        Category: category,
        File: item.file || "N/A",
        "Created At": formatDate(item.createdAt),
      };

      if (!categoryMap[category]) {
        categoryMap[category] = [];
      }
      categoryMap[category].push(row);
    });

    const workbook = XLSX.utils.book_new();

    Object.entries(categoryMap).forEach(([category, rows]) => {
      const sheet = XLSX.utils.json_to_sheet(rows);
      const safeSheetName =
        category.length > 31 ? category.slice(0, 31) : category;
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `CategoryWise_Syllabus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div class="modal-header d-flex justify-content-between">
                    <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                      Add Syllabus{" "}
                      <span className="ms-3 text-secondary">
                        ({String(filledFields).padStart(2, "0")}/
                        {String(totalFields).padStart(2, "0")})
                      </span>
                    </h5>
                    <div className="d-flex align-items-center">
                      <span class="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div class="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          class="form-check-input m-0"
                          checked
                        />
                      </div>
                      <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div class="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Class <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="Class"
                          value={
                            classList
                              .map((cls) => ({ label: cls, value: cls }))
                              .find((opt) => opt.value === formData.Class) ||
                            null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "Class",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={classList.map((cls) => ({
                            label: cls,
                            value: cls,
                          }))}
                          placeholder="Select Class"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Subject <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="subject"
                          value={
                            subjectList
                              .map((subject) => ({
                                label: subject,
                                value: subject,
                              }))
                              .find(
                                (opt) =>
                                  opt.value.toLowerCase().trim() ===
                                  formData.subject?.toLowerCase().trim(),
                              ) || null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "subject",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={subjectList.map((subject) => ({
                            label: subject,
                            value: subject,
                          }))}
                          placeholder="Select Subject"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Category <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="category"
                          value={
                            syllabusCategoryList
                              .map((category) => ({
                                label: category,
                                value: category,
                              }))
                              .find(
                                (opt) =>
                                  opt.value.toLowerCase().trim() ===
                                  formData.category?.toLowerCase().trim(),
                              ) || null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "category",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={syllabusCategoryList.map((category) => ({
                            label: category,
                            value: category,
                          }))}
                          placeholder="Select Category"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Upload File{" "}
                          {!isEditing && <span className="text-danger">*</span>}
                          {"  "}&nbsp;
                        </label>
                        <input
                          type="file"
                          id="fileInput"
                          className="form-control"
                          onChange={handleFileChange}
                          required={!isEditing}
                        />
                      </div>
                    </div>
                  </div>

                  <div class="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        class="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((syllabus, index) => (
                        <tr>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap">
                            <span class="text-dark">Syllabus ID</span> &mdash;{" "}
                            {syllabus.syllabusId}
                            <br />
                            <span class="text-dark">
                              Teacher ID
                            </span> &mdash; {syllabus.userId}
                            <br />
                            <span class="text-dark">Class</span> &mdash;{" "}
                            {syllabus.Class} <br />
                            <span class="text-dark">Subject</span> &mdash;{" "}
                            <span className="text-capitalize">
                              {syllabus.subject}
                            </span>
                            <br />
                            <span class="text-dark">Category</span> &mdash;{" "}
                            <span className="text-capitalize">
                              {syllabus.category}
                            </span>
                            <br />
                            {syllabus.file && (
                              <>
                                <span className="text-dark">File</span> &mdash;{" "}
                                <a
                                  href={syllabus.file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Click Here
                                </a>
                                <br />
                              </>
                            )}
                            <span class="text-dark">Date</span> &mdash;{" "}
                            {new Date(syllabus.createdAt).toLocaleDateString(
                              "en-GB",
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span
                                onClick={() => handleEditClick(syllabus)}
                                class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Update
                              </span>
                              <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        data-bs-toggle="modal"
                        onClick={resetForm}
                        data-bs-target=".gd-example-modal-lg"
                        class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Syllabus
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Syllabus</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Syllabus Details
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {fetchLoading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Syllabus
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            2.4 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportAllSyllabusExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                All Syllabus
                              </span>
                              <span
                                onClick={handleExportTeacherWiseSyllabusExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Teacher Wise Syllabus
                              </span>
                              <span
                                onClick={handleExportClassWiseSyllabusExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Class Wise
                              </span>
                              <span
                                onClick={handleExportSubjectWiseSyllabusExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Subject Wise
                              </span>
                              <span
                                onClick={handleExportCategoryWiseSyllabusExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Category Wise
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Syllabus;
